import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Palette,
  Image,
  Mail,
  FileText,
  Upload,
  RefreshCw,
  Eye,
  Trash2,
  ArrowLeft,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { cn } from "@/lib/utils";
import { brandingApi } from "@/services/api";

// Loading skeleton
const BrandingSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
      <Skeleton className="h-10 w-28" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-10 w-full max-w-md" />
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
        <Skeleton className="h-10 w-32" />
      </CardContent>
    </Card>
  </div>
);

// Color picker component
const ColorPicker = ({ label, value, onChange, id }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <div className="flex items-center gap-2">
      <div className="relative">
        <input
          aria-label="Colour"
          type="color"
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded-md cursor-pointer border border-input"
        />
      </div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        className="flex-1 font-mono text-sm"
      />
    </div>
  </div>
);

// Logo upload component
const LogoUpload = ({
  label,
  description,
  currentUrl,
  onUpload,
  onDelete,
  isLoading,
}) => {
  const { t } = useTranslation();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  return (
    <div className="flex items-start gap-4 p-4 border border-border rounded-lg">
      <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center overflow-hidden shrink-0">
        {currentUrl ? (
          <img
            src={currentUrl}
            alt={label}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <Image className="w-8 h-8 text-muted-foreground" aria-hidden="true" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-foreground">{label}</h4>
        <p className="text-sm text-muted-foreground mb-3">{description}</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild disabled={isLoading}>
            <label className="cursor-pointer">
              <Upload className="w-4 h-4 mr-2" aria-hidden="true" />
              {isLoading ? t("common.uploading") : t("common.upload")}
              <input
                aria-label="Upload a file"
                type="file"
                className="sr-only"
                accept="image/*"
                onChange={handleFileChange}
                disabled={isLoading}
              />
            </label>
          </Button>
          {currentUrl && onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// General settings tab
const GeneralSettings = ({ branding, onUpdate, isLoading }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: branding?.companyName || "",
    primaryColor: branding?.primaryColor || "#6366f1",
    secondaryColor: branding?.secondaryColor || "#8b5cf6",
    accentColor: branding?.accentColor || "#06b6d4",
    fontFamily: branding?.fontFamily || "Inter, sans-serif",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
  };

  const fonts = [
    { value: "Inter, sans-serif", label: "Inter" },
    { value: "'Roboto', sans-serif", label: "Roboto" },
    { value: "'Open Sans', sans-serif", label: "Open Sans" },
    { value: "'Lato', sans-serif", label: "Lato" },
    { value: "'Poppins', sans-serif", label: "Poppins" },
    { value: "'Plus Jakarta Sans', sans-serif", label: "Plus Jakarta Sans" },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="companyName">
          {t("settings.branding.companyName")}
        </Label>
        <Input
          id="companyName"
          value={formData.companyName}
          onChange={(e) =>
            setFormData({ ...formData, companyName: e.target.value })
          }
          placeholder={t("placeholders.yourCompany")}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ColorPicker
          id="primaryColor"
          label={t("settings.branding.primaryColor")}
          value={formData.primaryColor}
          onChange={(color) =>
            setFormData({ ...formData, primaryColor: color })
          }
        />
        <ColorPicker
          id="secondaryColor"
          label={t("settings.branding.secondaryColor")}
          value={formData.secondaryColor}
          onChange={(color) =>
            setFormData({ ...formData, secondaryColor: color })
          }
        />
        <ColorPicker
          id="accentColor"
          label={t("settings.branding.accentColor")}
          value={formData.accentColor}
          onChange={(color) => setFormData({ ...formData, accentColor: color })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="fontFamily">{t("settings.branding.fontFamily")}</Label>
        <Select
          value={formData.fontFamily}
          onValueChange={(value) =>
            setFormData({ ...formData, fontFamily: value })
          }
        >
          <SelectTrigger id="fontFamily">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fonts.map((font) => (
              <SelectItem key={font.value} value={font.value}>
                <span style={{ fontFamily: font.value }}>{font.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button type="submit" disabled={isLoading}>
        {isLoading && (
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
        )}
        {isLoading ? t("common.saving") : t("common.saveChanges")}
      </Button>
    </form>
  );
};

// Logo settings tab
const LogoSettings = ({ branding, onUpload, onDelete, isLoading }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <LogoUpload
        label={t("settings.branding.mainLogo")}
        description={t("settings.branding.mainLogoDesc")}
        currentUrl={branding?.logoUrl}
        onUpload={(file) => onUpload(file, "logo")}
        onDelete={() => onDelete("logo")}
        isLoading={isLoading}
      />
      <LogoUpload
        label={t("settings.branding.darkModeLogo")}
        description={t("settings.branding.darkModeLogoDesc")}
        currentUrl={branding?.logoDarkUrl}
        onUpload={(file) => onUpload(file, "logo-dark")}
        onDelete={() => onDelete("logo-dark")}
        isLoading={isLoading}
      />
      <LogoUpload
        label={t("settings.branding.favicon")}
        description={t("settings.branding.faviconDesc")}
        currentUrl={branding?.faviconUrl}
        onUpload={(file) => onUpload(file, "favicon")}
        onDelete={() => onDelete("favicon")}
        isLoading={isLoading}
      />
      <LogoUpload
        label={t("settings.branding.emailLogo")}
        description={t("settings.branding.emailLogoDesc")}
        currentUrl={branding?.emailLogoUrl}
        onUpload={(file) => onUpload(file, "email-logo")}
        onDelete={() => onDelete("email-logo")}
        isLoading={isLoading}
      />
    </div>
  );
};

// Email settings tab
const EmailSettings = ({ branding, onUpdate, isLoading }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    emailFromName: branding?.emailFromName || "",
    emailFromAddress: branding?.emailFromAddress || "",
    emailReplyTo: branding?.emailReplyTo || "",
    emailFooterText: branding?.emailFooterText || "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="emailFromName">
          {t("settings.branding.emailFromName")}
        </Label>
        <Input
          id="emailFromName"
          value={formData.emailFromName}
          onChange={(e) =>
            setFormData({ ...formData, emailFromName: e.target.value })
          }
          placeholder={t("placeholders.yourCompany")}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="emailFromAddress">
          {t("settings.branding.emailFromAddress")}
        </Label>
        <Input
          id="emailFromAddress"
          type="email"
          value={formData.emailFromAddress}
          onChange={(e) =>
            setFormData({ ...formData, emailFromAddress: e.target.value })
          }
          placeholder="noreply@yourcompany.com"
        />
        <p className="text-xs text-muted-foreground">
          {t("settings.branding.emailFromAddressHint")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="emailReplyTo">
          {t("settings.branding.emailReplyTo")}
        </Label>
        <Input
          id="emailReplyTo"
          type="email"
          value={formData.emailReplyTo}
          onChange={(e) =>
            setFormData({ ...formData, emailReplyTo: e.target.value })
          }
          placeholder="support@yourcompany.com"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="emailFooterText">
          {t("settings.branding.emailFooterText")}
        </Label>
        <Textarea
          id="emailFooterText"
          value={formData.emailFooterText}
          onChange={(e) =>
            setFormData({ ...formData, emailFooterText: e.target.value })
          }
          rows={3}
          placeholder="© 2024 Your Company. All rights reserved."
        />
      </div>

      <Button type="submit" disabled={isLoading}>
        {isLoading && (
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
        )}
        {isLoading ? t("common.saving") : t("common.saveChanges")}
      </Button>
    </form>
  );
};

// Signing page settings tab
const SigningPageSettings = ({ branding, onUpdate, isLoading }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    signingPageTitle: branding?.signingPageTitle || "",
    signingPageWelcomeMessage: branding?.signingPageWelcomeMessage || "",
    signingPageCompletionMessage: branding?.signingPageCompletionMessage || "",
    hidePoweredBy: branding?.hidePoweredBy || false,
    customTermsUrl: branding?.customTermsUrl || "",
    customPrivacyUrl: branding?.customPrivacyUrl || "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="signingPageTitle">
          {t("settings.branding.signingPageTitle")}
        </Label>
        <Input
          id="signingPageTitle"
          value={formData.signingPageTitle}
          onChange={(e) =>
            setFormData({ ...formData, signingPageTitle: e.target.value })
          }
          placeholder={t("placeholders.signDocument")}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="signingPageWelcomeMessage">
          {t("settings.branding.welcomeMessage")}
        </Label>
        <Textarea
          id="signingPageWelcomeMessage"
          value={formData.signingPageWelcomeMessage}
          onChange={(e) =>
            setFormData({
              ...formData,
              signingPageWelcomeMessage: e.target.value,
            })
          }
          rows={2}
          placeholder={t("settings.branding.welcomeMessagePlaceholder")}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="signingPageCompletionMessage">
          {t("settings.branding.completionMessage")}
        </Label>
        <Textarea
          id="signingPageCompletionMessage"
          value={formData.signingPageCompletionMessage}
          onChange={(e) =>
            setFormData({
              ...formData,
              signingPageCompletionMessage: e.target.value,
            })
          }
          rows={2}
          placeholder={t("placeholders.thankYouForSigning")}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customTermsUrl">
            {t("settings.branding.termsUrl")}
          </Label>
          <Input
            id="customTermsUrl"
            type="url"
            value={formData.customTermsUrl}
            onChange={(e) =>
              setFormData({ ...formData, customTermsUrl: e.target.value })
            }
            placeholder="https://yoursite.com/terms"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="customPrivacyUrl">
            {t("settings.branding.privacyUrl")}
          </Label>
          <Input
            id="customPrivacyUrl"
            type="url"
            value={formData.customPrivacyUrl}
            onChange={(e) =>
              setFormData({ ...formData, customPrivacyUrl: e.target.value })
            }
            placeholder="https://yoursite.com/privacy"
          />
        </div>
      </div>

      <div className="flex items-center justify-between p-4 border border-border rounded-lg">
        <div className="space-y-0.5">
          <Label htmlFor="hidePoweredBy" className="cursor-pointer">
            {t("settings.branding.hidePoweredBy")}
          </Label>
          <p className="text-sm text-muted-foreground">
            {t("settings.branding.hidePoweredByDesc")}
          </p>
        </div>
        <Switch
          id="hidePoweredBy"
          checked={formData.hidePoweredBy}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, hidePoweredBy: checked })
          }
        />
      </div>

      <Button type="submit" disabled={isLoading}>
        {isLoading && (
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
        )}
        {isLoading ? t("common.saving") : t("common.saveChanges")}
      </Button>
    </form>
  );
};

// Preview dialog
const BrandingPreviewDialog = ({ branding, open, onOpenChange }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("settings.branding.preview")}</DialogTitle>
        </DialogHeader>
        <div
          className="p-8 border border-border rounded-lg"
          style={{
            "--brand-primary": branding?.primaryColor || "#6366f1",
            fontFamily: branding?.fontFamily || "Inter, sans-serif",
          }}
        >
          <div className="text-center mb-8">
            {branding?.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt="Logo"
                className="h-12 mx-auto mb-4"
              />
            ) : (
              <div className="h-12 w-32 bg-muted rounded mx-auto mb-4 flex items-center justify-center">
                <Image
                  className="w-6 h-6 text-muted-foreground"
                  aria-hidden="true"
                />
              </div>
            )}
            <h1
              className="text-2xl font-bold"
              style={{ color: branding?.primaryColor }}
            >
              {branding?.signingPageTitle || t("signing.signDocument")}
            </h1>
            <p className="text-muted-foreground mt-2">
              {branding?.signingPageWelcomeMessage ||
                t("settings.branding.signingPageMessageDefault")}
            </p>
          </div>

          <div className="bg-muted rounded-lg p-8 text-center">
            <FileText
              className="w-16 h-16 text-muted-foreground mx-auto mb-4"
              aria-hidden="true"
            />
            <p className="text-muted-foreground">
              {t("documents.previewArea")}
            </p>
          </div>

          {!branding?.hidePoweredBy && (
            <p className="text-center text-xs text-muted-foreground mt-8">
              Powered by eScribe
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main component
export const BrandingSettings = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("general");
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: branding, isLoading } = useQuery({
    queryKey: ["branding"],
    queryFn: () => brandingApi.get().then((res) => res.data?.data),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => brandingApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["branding"] });
      toast.success(t("settings.branding.saved"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          t("settings.branding.saveFailed"),
      );
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, type }) => brandingApi.uploadLogo(type, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["branding"] });
      toast.success(t("settings.branding.logoUploaded"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          t("settings.branding.logoUploadFailed"),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (type) => brandingApi.deleteLogo(type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["branding"] });
      toast.success(t("settings.branding.logoDeleted"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          t("settings.branding.logoDeleteFailed"),
      );
    },
  });

  if (isLoading) {
    return <BrandingSkeleton />;
  }

  const tabs = [
    {
      id: "general",
      label: t("settings.branding.tabs.general"),
      icon: Palette,
    },
    { id: "logos", label: t("settings.branding.tabs.logos"), icon: Image },
    { id: "emails", label: t("settings.branding.tabs.emails"), icon: Mail },
    {
      id: "signing",
      label: t("settings.branding.tabs.signingPage"),
      icon: FileText,
    },
  ];

  const handleUpload = (file, type) => {
    uploadMutation.mutate({ file, type });
  };

  const handleDelete = (type) => {
    deleteMutation.mutate(type);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return (
          <GeneralSettings
            branding={branding}
            onUpdate={(data) => updateMutation.mutate(data)}
            isLoading={updateMutation.isPending}
          />
        );
      case "logos":
        return (
          <LogoSettings
            branding={branding}
            onUpload={handleUpload}
            onDelete={handleDelete}
            isLoading={uploadMutation.isPending || deleteMutation.isPending}
          />
        );
      case "emails":
        return (
          <EmailSettings
            branding={branding}
            onUpdate={(data) => updateMutation.mutate(data)}
            isLoading={updateMutation.isPending}
          />
        );
      case "signing":
        return (
          <SigningPageSettings
            branding={branding}
            onUpdate={(data) => updateMutation.mutate(data)}
            isLoading={updateMutation.isPending}
          />
        );
      default:
        return null;
    }
  };

  return (
    <main className="space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/settings"
            className="flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-lg border hover:bg-accent transition-colors touch-target"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-heading tracking-tight">
              {t("settings.branding.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("settings.branding.subtitle")}
            </p>
          </div>
        </div>
        {!isMobile && (
          <Button variant="outline" onClick={() => setPreviewOpen(true)}>
            <Eye className="w-4 h-4 mr-2" aria-hidden="true" />
            {t("common.preview")}
          </Button>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="border-b">
        <nav
          className="-mb-px flex space-x-1 overflow-x-auto"
          aria-label="Branding tabs"
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors",
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30",
                )}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <Card>
        <CardContent className="pt-6">{renderTabContent()}</CardContent>
      </Card>

      {/* Preview Dialog */}
      <BrandingPreviewDialog
        branding={branding}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
      />
    </main>
  );
};

export default BrandingSettings;
